import '../styles/DashboardTransactionLedger.css';
import React from 'react';
import { useProjectCollection } from '../lib/projectData';

const LEDGER_COLLECTION = 'ledger_entries';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDate(value) {
  if (typeof value !== 'string' || value.length === 0) return '—';
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  const monthIndex = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  const month = MONTHS[monthIndex] || parts[1];
  if (Number.isNaN(day)) return value;
  return `${month} ${day}`;
}

function formatAmount(value) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatBalance(value) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function description(item) {
  if (item && typeof item.description === 'string' && item.description.length > 0) {
    return item.description;
  }
  return 'Ledger entry';
}

function counterpartyLabel(item) {
  if (item && typeof item.counterparty === 'string' && item.counterparty.length > 0) {
    return item.counterparty;
  }
  return '';
}

function isCredit(item) {
  return item && item.direction === 'credit';
}

function statusLabel(item) {
  if (item && typeof item.status === 'string' && item.status.length > 0) {
    return item.status;
  }
  return 'recorded';
}

function RetryIcon() {
  return (
    <svg
      className="dtl-retry-icon"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M13 8a5 5 0 1 1-1.6-3.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M13.2 2.2v2.9h-2.9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LedgerRows({ entries }) {
  return (
    <ul className="dtl-rows">
      {entries.map((item) => {
        const credit = isCredit(item);
        const amountClass = credit ? 'dtl-amount is-credit' : 'dtl-amount is-debit';
        const dirClass = credit ? 'dtl-dir is-credit' : 'dtl-dir is-debit';
        const counterparty = counterpartyLabel(item);
        return (
          <li className="dtl-row" key={item.id} tabIndex={0}>
            <div className="dtl-cell-label">
              <div className="dtl-row-top">
                <span className="dtl-date">{formatDate(item.date)}</span>
                <span className="dtl-desc">{description(item)}</span>
              </div>
              <div className="dtl-meta">
                {counterparty ? (
                  <span className="dtl-counterparty">
                    {credit ? '←' : '→'} {counterparty}
                  </span>
                ) : null}
                <span className="dtl-status">{statusLabel(item)}</span>
              </div>
            </div>
            <div className="dtl-cell-amount">
              <span className={amountClass}>{formatAmount(item.amount)}</span>
              <span className={dirClass}>{credit ? 'Credit' : 'Debit'}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function LedgerLoading() {
  return (
    <div className="dtl-rows" role="status" aria-live="polite" aria-label="Loading ledger entries">
      <div className="dtl-skel-row">
        <div className="dtl-skel-label">
          <span className="dtl-skel-bar is-long" />
          <span className="dtl-skel-bar is-short" />
        </div>
        <span className="dtl-skel-amount" />
      </div>
      <div className="dtl-skel-row">
        <div className="dtl-skel-label">
          <span className="dtl-skel-bar is-long" />
          <span className="dtl-skel-bar is-short" />
        </div>
        <span className="dtl-skel-amount" />
      </div>
      <div className="dtl-skel-row">
        <div className="dtl-skel-label">
          <span className="dtl-skel-bar is-long" />
          <span className="dtl-skel-bar is-short" />
        </div>
        <span className="dtl-skel-amount" />
      </div>
      <span className="dtl-visually-hidden">Reading ledger entries…</span>
    </div>
  );
}

function LedgerNotice({ tone, signal, title, body, onRetry }) {
  return (
    <div className="dtl-notice">
      <span className={tone === 'error' ? 'dtl-notice-mark is-error' : 'dtl-notice-mark'}>
        {signal}
      </span>
      <p className="dtl-notice-text">{title}</p>
      <p className="dtl-notice-sub">{body}</p>
      {onRetry ? (
        <button type="button" className="dtl-retry" onClick={onRetry}>
          <RetryIcon />
          Retry ledger load
        </button>
      ) : null}
    </div>
  );
}

export default function DashboardTransactionLedger({ accountId, openingBalance }) {
  const { data, loading, error, refresh } = useProjectCollection(LEDGER_COLLECTION);

  const entries = Array.isArray(data) ? data : [];
  const hasEntries = !loading && !error && entries.length > 0;

  let branch = 'empty';
  if (loading) branch = 'loading';
  else if (error) branch = 'error';
  else if (entries.length > 0) branch = 'populated';

  const hasOpening = Number.isFinite(Number(openingBalance));

  return (
    <section className="dtl-root" aria-labelledby="dtl-heading">
      <div className="dtl-inner">
        <div className="dtl-head">
          <div className="dtl-head-lock">
            <h2 className="dtl-micro" id="dtl-heading">
              Transaction Ledger
            </h2>
            <p className="dtl-count">
              {hasEntries
                ? `${entries.length} recorded ${entries.length === 1 ? 'entry' : 'entries'}`
                : 'Past account activity, ruled and measured'}
            </p>
          </div>
          <div className="dtl-head-scale" aria-hidden="true">
            <span className="dtl-scale-tick" />
            <span className="dtl-scale-tick is-long" />
            <span className="dtl-scale-tick" />
            <span className="dtl-scale-tick is-lit" />
            <span className="dtl-scale-tick" />
            <span className="dtl-scale-tick is-long" />
            <span className="dtl-scale-tick" />
          </div>
        </div>

        <div className="dtl-columns">
          <span className="dtl-col-head">Date / Entry</span>
          <span className="dtl-col-head is-amount">Amount</span>
        </div>

        {branch === 'loading' ? <LedgerLoading /> : null}

        {branch === 'error' ? (
          <LedgerNotice
            tone="error"
            signal="Ledger unavailable"
            title="The transaction ledger could not be read."
            body="No readings are shown rather than a partial list. Retry the load to read the ledger again."
            onRetry={refresh}
          />
        ) : null}

        {branch === 'empty' ? (
          <LedgerNotice
            tone="neutral"
            signal="No entries"
            title="No transfers have been made yet."
            body="Once funds move in or out of this account, each entry is recorded here as a ruled instrument reading."
          />
        ) : null}

        {branch === 'populated' ? <LedgerRows entries={entries} /> : null}

        <div className="dtl-foot">
          <span className="dtl-foot-note">
            {hasOpening
              ? `Opening balance ${formatBalance(openingBalance)}${accountId ? ' · ' + accountId : ''}`
              : accountId
                ? `Account ${accountId}`
                : 'Account ledger'}
          </span>
          <span className="dtl-foot-rule" aria-hidden="true" />
          <span className="dtl-foot-note">
            {branch === 'populated' ? 'Rubric · settled activity' : 'Ledger read state'}
          </span>
        </div>
      </div>
    </section>
  );
}